import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Cruise } from '../context/CruiseContext';
import { debug } from '../utils/debug';

// In-memory cache across hook instances to prevent duplicate Firestore reads
const itineraryTitleCache = new Map<string, string | null>();

/**
 * Resolves the marketing itinerary title (e.g., "7 Night Western Caribbean")
 * for a cruise, either from the cruise document itself or by looking up
 * the official itinerary document in Firestore.
 */
export const useCruiseItineraryTitle = (cruise: Cruise | null | undefined): string | null => {
  const initialTitle = cruise?.itineraryTitle || (cruise?.itineraryKey ? itineraryTitleCache.get(cruise.itineraryKey) ?? null : null);
  const [itineraryTitle, setItineraryTitle] = useState<string | null>(initialTitle);

  useEffect(() => {
    if (!cruise) {
      setItineraryTitle(null);
      return;
    }

    // Direct property on cruise
    if (cruise.itineraryTitle) {
      setItineraryTitle(cruise.itineraryTitle);
      if (cruise.itineraryKey) {
        itineraryTitleCache.set(cruise.itineraryKey, cruise.itineraryTitle);
      }
      return;
    }

    // No itineraryKey to look up
    if (!cruise.itineraryKey) {
      setItineraryTitle(null);
      return;
    }

    // Check cache
    if (itineraryTitleCache.has(cruise.itineraryKey)) {
      setItineraryTitle(itineraryTitleCache.get(cruise.itineraryKey) ?? null);
      return;
    }

    let isMounted = true;

    const fetchTitle = async () => {
      try {
        const itinRef = doc(db, 'itineraries', cruise.itineraryKey!);
        const snap = await getDoc(itinRef);
        if (snap.exists()) {
          const data = snap.data();
          const title = data?.itineraryTitle || null;
          itineraryTitleCache.set(cruise.itineraryKey!, title);
          if (isMounted) {
            setItineraryTitle(title);
          }
        } else {
          itineraryTitleCache.set(cruise.itineraryKey!, null);
          if (isMounted) {
            setItineraryTitle(null);
          }
        }
      } catch (err) {
        debug.warn('[useCruiseItineraryTitle] Failed to fetch itinerary title:', err);
        if (isMounted) {
          setItineraryTitle(null);
        }
      }
    };

    fetchTitle();

    return () => {
      isMounted = false;
    };
  }, [cruise?.id, cruise?.itineraryKey, cruise?.itineraryTitle]);

  return itineraryTitle;
};
